/**
 * Permission system for role-based access control.
 *
 * Resolves permissions from backend (/permissions/me) with a static-role
 * fallback for resilience.
 */
import { useEffect, useMemo, useState } from 'react'
import api from '../api'
import { useAuthStore } from '../store'

export type Permission =
  | 'beneficiaries:view' | 'beneficiaries:manage'
  | 'claims:view' | 'claims:manage' | 'claims:approve'
  | 'plans:view' | 'plans:manage' | 'plans:approve'
  | 'broadcasts:view' | 'broadcasts:manage'
  | 'reports:view' | 'reports:export'
  | 'settings:view' | 'settings:manage'
  | 'users:view' | 'users:manage'
  | 'audit:view'
  | 'fee_structures:view' | 'fee_structures:manage'
  | 'sessions:view'
  | 'escalations:view' | 'escalations:manage'
  | 'pledges:view' | 'pledges:manage'
  | 'balance:upload'
  | 'segments:view' | 'segments:manage'
  // Education
  | 'results:view' | 'results:manage'
  | 'attendance:view' | 'attendance:manage'
  | 'classes:view' | 'classes:manage'
  | 'timetable:view' | 'timetable:manage'
  | 'discipline:view' | 'discipline:manage'
  | 'assignments:view' | 'assignments:manage'
  // Platform
  | 'platform:admin' | 'platform:impersonate'
  | 'tenant:view_as'

type UserRole = 'super_admin' | 'admin' | 'principal' | 'bursar' | 'class_teacher' | 'dorm_master' | 'teacher' | 'viewer'

const VIEWER_PERMISSIONS: Permission[] = [
  'beneficiaries:view', 'claims:view', 'plans:view', 'broadcasts:view',
  'reports:view', 'fee_structures:view', 'sessions:view', 'escalations:view',
  'pledges:view', 'segments:view', 'assignments:view',
]

const TEACHER_PERMISSIONS: Permission[] = [
  'beneficiaries:view', 'sessions:view',
  'results:view', 'results:manage',
  'attendance:view', 'attendance:manage',
  'classes:view', 'timetable:view', 'discipline:view',
  'assignments:view', 'assignments:manage',
]

const CLASS_TEACHER_PERMISSIONS: Permission[] = [
  ...TEACHER_PERMISSIONS,
  'claims:view', 'escalations:view', 'pledges:view',
  'discipline:manage',
]

const DORM_MASTER_PERMISSIONS: Permission[] = [
  'beneficiaries:view', 'sessions:view',
  'attendance:view', 'attendance:manage',
  'classes:view', 'discipline:view', 'discipline:manage',
]

const BURSAR_PERMISSIONS: Permission[] = [
  ...VIEWER_PERMISSIONS,
  'beneficiaries:manage', 'claims:manage', 'claims:approve',
  'plans:manage', 'reports:export',
  'fee_structures:manage', 'pledges:manage', 'balance:upload',
  'results:view', 'attendance:view', 'classes:view',
]

const PRINCIPAL_PERMISSIONS: Permission[] = [
  ...VIEWER_PERMISSIONS,
  'broadcasts:manage', 'escalations:manage', 'users:view',
  'results:view', 'results:manage',
  'attendance:view', 'attendance:manage',
  'classes:view', 'classes:manage',
  'timetable:view', 'timetable:manage',
  'discipline:view', 'discipline:manage',
  'assignments:manage',
]

const ADMIN_PERMISSIONS: Permission[] = [
  ...BURSAR_PERMISSIONS,
  'plans:approve', 'broadcasts:manage',
  'settings:view', 'settings:manage',
  'users:view', 'users:manage', 'audit:view',
  'escalations:manage', 'segments:manage',
  'results:manage', 'attendance:manage',
  'classes:manage', 'timetable:view', 'timetable:manage',
  'discipline:view', 'discipline:manage',
  'assignments:manage',
  'tenant:view_as',
]

const ALL_PERMISSIONS: Permission[] = [
  ...ADMIN_PERMISSIONS,
  'platform:admin', 'platform:impersonate',
]

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  viewer: VIEWER_PERMISSIONS,
  teacher: TEACHER_PERMISSIONS,
  class_teacher: CLASS_TEACHER_PERMISSIONS,
  dorm_master: DORM_MASTER_PERMISSIONS,
  bursar: BURSAR_PERMISSIONS,
  principal: PRINCIPAL_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
  super_admin: ALL_PERMISSIONS,
}

const PERMISSION_CACHE_TTL_MS = 60 * 1000
const permissionCache = new Map<string, { permissions: Permission[]; fetchedAt: number }>()
const inflightFetches = new Map<string, Promise<Permission[]>>()

interface PermissionsMeResponse {
  role: string
  permissions: string[]
}

function isPermission(value: string): value is Permission {
  return (
    value === 'beneficiaries:view' ||
    value === 'beneficiaries:manage' ||
    value === 'claims:view' ||
    value === 'claims:manage' ||
    value === 'claims:approve' ||
    value === 'plans:view' ||
    value === 'plans:manage' ||
    value === 'plans:approve' ||
    value === 'broadcasts:view' ||
    value === 'broadcasts:manage' ||
    value === 'reports:view' ||
    value === 'reports:export' ||
    value === 'settings:view' ||
    value === 'settings:manage' ||
    value === 'users:view' ||
    value === 'users:manage' ||
    value === 'audit:view' ||
    value === 'fee_structures:view' ||
    value === 'fee_structures:manage' ||
    value === 'sessions:view' ||
    value === 'escalations:view' ||
    value === 'escalations:manage' ||
    value === 'pledges:view' ||
    value === 'pledges:manage' ||
    value === 'balance:upload' ||
    value === 'segments:view' ||
    value === 'segments:manage' ||
    value === 'results:view' ||
    value === 'results:manage' ||
    value === 'attendance:view' ||
    value === 'attendance:manage' ||
    value === 'classes:view' ||
    value === 'classes:manage' ||
    value === 'timetable:view' ||
    value === 'timetable:manage' ||
    value === 'discipline:view' ||
    value === 'discipline:manage' ||
    value === 'assignments:view' ||
    value === 'assignments:manage' ||
    value === 'platform:admin' ||
    value === 'platform:impersonate' ||
    value === 'tenant:view_as'
  )
}

function permissionCacheKey(role: UserRole, tenantId: string | null): string {
  return `${tenantId || 'no-tenant'}:${role}`
}

async function fetchPermissionsForRole(role: UserRole, tenantId: string | null): Promise<Permission[]> {
  const key = permissionCacheKey(role, tenantId)
  const cached = permissionCache.get(key)
  if (cached && Date.now() - cached.fetchedAt < PERMISSION_CACHE_TTL_MS) {
    return cached.permissions
  }

  const inflight = inflightFetches.get(key)
  if (inflight) {
    return inflight
  }

  const request = (async () => {
    const response = await api.get<PermissionsMeResponse>('/permissions/me')
    const resolved = (response.data.permissions || []).filter(isPermission)
    permissionCache.set(key, { permissions: resolved, fetchedAt: Date.now() })
    return resolved
  })()

  inflightFetches.set(key, request)
  try {
    return await request
  } finally {
    inflightFetches.delete(key)
  }
}

function useResolvedPermissions(): Permission[] {
  const role = useAuthStore((s) => s.user?.role) as UserRole | undefined
  const tenantId = useAuthStore((s) => s.user?.tenant_id || null)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const fallback = role ? ROLE_PERMISSIONS[role] || [] : []

  const [dynamicPermissions, setDynamicPermissions] = useState<Permission[] | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!isAuthenticated || !role) {
      setDynamicPermissions(null)
      return
    }

    fetchPermissionsForRole(role, tenantId)
      .then((permissions) => {
        if (!cancelled) setDynamicPermissions(permissions)
      })
      .catch(() => {
        if (!cancelled) setDynamicPermissions(null)
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, role, tenantId])

  return dynamicPermissions ?? fallback
}

/**
 * Check if the current user has a specific permission.
 */
export function usePermission(permission: Permission): boolean {
  const role = useAuthStore((s) => s.user?.role) as UserRole | undefined
  const permissions = useResolvedPermissions()
  if (!role) return false
  return permissions.includes(permission)
}

/**
 * Check if the current user has ALL of the given permissions.
 */
export function usePermissions(permissions: Permission[]): boolean {
  const role = useAuthStore((s) => s.user?.role) as UserRole | undefined
  const resolved = useResolvedPermissions()
  const resolvedSet = useMemo(() => new Set(resolved), [resolved])
  if (!role) return false
  return permissions.every((p) => resolvedSet.has(p))
}

/**
 * Get all permissions for the current user.
 */
export function useAllPermissions(): Permission[] {
  const role = useAuthStore((s) => s.user?.role) as UserRole | undefined
  const resolved = useResolvedPermissions()
  if (!role) return []
  return resolved
}
